// @ts-nocheck
import React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';

const DARK_BG = `
  radial-gradient(circle at 80% 20%, rgba(30, 80, 255, 0.35) 0%, transparent 50%),
  radial-gradient(circle at 20% 80%, rgba(0, 50, 150, 0.25) 0%, transparent 60%),
  linear-gradient(135deg, #020c1b 0%, #122c4a 100%)
`;

const LIGHT_BG = '#F2F3F5';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  min-height: 100%;
  overflow: auto;
  box-sizing: border-box;
  padding: ${rem('48px')} ${rem('56px')} ${rem('24px')};
  background: ${LIGHT_BG};

  body[data-theme='dark'] & {
    background: ${DARK_BG};
  }

  @media (max-width: 640px) {
    padding: ${rem('32px')} ${rem('20px')} ${rem('20px')};
  }
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  max-width: ${rem('920px')};
  margin: 0 auto;
  justify-content: center;
  transform: translateY(${rem('-56px')});

  @media (max-width: 640px) {
    transform: translateY(${rem('-32px')});
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${rem('20px')};
  width: 100%;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const skeletonPulse = `
  @keyframes landingSkeletonPulse {
    0%, 100% { opacity: 0.45; }
    50% { opacity: 0.85; }
  }
`;

const SkeletonBlock = styled.div`
  ${skeletonPulse}
  border-radius: ${rem('8px')};
  animation: landingSkeletonPulse 1.4s ease-in-out infinite;
  background: rgba(15, 23, 42, 0.08);

  body[data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SkeletonPill = styled(SkeletonBlock)`
  width: ${rem('180px')};
  height: ${rem('40px')};
  border-radius: ${rem('999px')};
  margin-bottom: ${rem('20px')};
`;

const SkeletonTitle = styled(SkeletonBlock)`
  width: 72%;
  max-width: ${rem('520px')};
  height: ${rem('55px')};
  margin-bottom: ${rem('12px')};

  @media (max-width: 640px) {
    height: ${rem('34px')};
  }
`;

const SkeletonTitleSecond = styled(SkeletonBlock)`
  width: 42%;
  max-width: ${rem('280px')};
  height: ${rem('55px')};
  margin-bottom: ${rem('12px')};

  @media (max-width: 640px) {
    height: ${rem('34px')};
  }
`;

const SkeletonSubtitle = styled(SkeletonBlock)`
  width: 88%;
  max-width: ${rem('560px')};
  height: ${rem('16px')};
  margin-bottom: ${rem('10px')};
`;

const SkeletonSubtitleSecond = styled(SkeletonBlock)`
  width: 64%;
  max-width: ${rem('400px')};
  height: ${rem('16px')};
  margin-bottom: ${rem('36px')};
`;

const SkeletonCard = styled.div`
  ${skeletonPulse}
  min-height: ${rem('180px')};
  padding: ${rem('24px')};
  border-radius: ${rem('16px')};
  box-sizing: border-box;
  animation: landingSkeletonPulse 1.4s ease-in-out infinite;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.04);
  box-shadow: 0 ${rem('8px')} ${rem('24px')} rgba(15, 23, 42, 0.06);

  body[data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(173, 198, 255, 0.18);
    box-shadow: none;
  }
`;

const SkeletonCardIcon = styled.div`
  width: ${rem('56px')};
  height: ${rem('56px')};
  margin-bottom: ${rem('20px')};
  border-radius: ${rem('12px')};
  background: rgba(15, 23, 42, 0.08);

  body[data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const SkeletonCardTitle = styled.div`
  width: 55%;
  height: ${rem('26px')};
  margin-bottom: ${rem('14px')};
  border-radius: ${rem('6px')};
  background: rgba(15, 23, 42, 0.1);

  body[data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.14);
  }
`;

const SkeletonCardDesc = styled.div`
  width: 85%;
  height: ${rem('16px')};
  margin-bottom: ${rem('8px')};
  border-radius: ${rem('6px')};
  background: rgba(15, 23, 42, 0.06);

  body[data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const SkeletonCardDescShort = styled(SkeletonCardDesc)`
  width: 60%;
  margin-bottom: 0;
`;

const SkeletonFooter = styled(SkeletonBlock)`
  width: ${rem('220px')};
  height: ${rem('12px')};
  margin-top: ${rem('40px')};
`;

/**
 * Skeleton de welcome (/) — dark y light vía body[data-theme].
 * Usado en Suspense del router y mientras el theme aún no está resuelto.
 */
export function LandingPageSkeleton() {
  return (
    <PageContainer role="status" aria-busy="true" aria-label="Loading">
      <PageContent>
        <SkeletonPill />
        <SkeletonTitle />
        <SkeletonTitleSecond />
        <SkeletonSubtitle />
        <SkeletonSubtitleSecond />
        <CardsGrid>
          <SkeletonCard>
            <SkeletonCardIcon />
            <SkeletonCardTitle />
            <SkeletonCardDesc />
            <SkeletonCardDescShort />
          </SkeletonCard>
          <SkeletonCard>
            <SkeletonCardIcon />
            <SkeletonCardTitle />
            <SkeletonCardDesc />
            <SkeletonCardDescShort />
          </SkeletonCard>
        </CardsGrid>
      </PageContent>
      <SkeletonFooter />
    </PageContainer>
  );
}

export default LandingPageSkeleton;
